/**
 * Justesims header "Destinations" mega menu.
 *
 * Desktop-only interaction: the trigger link (data-je-mega-trigger) still
 * has a real href to /pages/all-destinations, so it works with no JS, on
 * mobile (where the drawer just navigates), and for keyboard/middle-click
 * users. On desktop with JS, clicking it opens the panel instead.
 *
 * Tab switching mirrors assets/je-country-picker.js's small a11y pattern.
 * Search-as-you-type filtering mirrors assets/je-hero.js's autocomplete —
 * show/hide tiles by text match, no page navigation.
 */
document.addEventListener('DOMContentLoaded', () => {
  const trigger = document.querySelector('[data-je-mega-trigger="destinations"]');
  const panel = document.getElementById('je-header-mega-destinations');
  if (!trigger || !panel) return;

  const backdrop = document.querySelector('[data-je-mega-backdrop]');
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const tabs = Array.from(panel.querySelectorAll('[data-je-mega-tabs] [role="tab"]'));
  const tabPanels = Array.from(panel.querySelectorAll('[data-je-mega-panels] > [role="tabpanel"]'));
  const searchInput = panel.querySelector('[data-je-mega-search]');
  const searchPanel = panel.querySelector('[data-je-mega-search-panel]');
  const searchGrid = panel.querySelector('[data-je-mega-search-grid]');
  const searchEmpty = panel.querySelector('[data-je-mega-empty]');
  const searchTiles = searchGrid ? Array.from(searchGrid.querySelectorAll('li')) : [];

  function openPanel() {
    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    if (backdrop) {
      // --header-height (used as a CSS fallback) only covers the header row
      // itself, not the announcement bar above it, since those render as
      // separate sibling sections rather than one wrapped block — using it
      // left a dark strip where the backdrop started too high, overlapping
      // the announcement bar/header instead of starting right below them.
      // .header-wrapper's actual rendered position already accounts for
      // whatever sits above it in normal document flow, so measure that
      // directly instead of trusting the CSS var.
      const headerWrapper = document.querySelector('.header-wrapper');
      if (headerWrapper) {
        backdrop.style.top = headerWrapper.getBoundingClientRect().bottom + 'px';
      }
      backdrop.classList.add('is-visible');
    }
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onKeydown);
  }

  function closePanel() {
    panel.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    if (backdrop) backdrop.classList.remove('is-visible');
    document.removeEventListener('click', onDocumentClick);
    document.removeEventListener('keydown', onKeydown);
  }

  function onDocumentClick(e) {
    // A real click's e.target is often a descendant of the trigger (e.g. the
    // inner <span>Destinations</span>), not the <a> itself — trigger.contains
    // catches that. e.target !== trigger alone only matched a click on the
    // exact <a> node, which is why this fired (and closed the panel the
    // instant it opened) for every real mouse click but not for a
    // script-dispatched trigger.click(), which targets the <a> directly.
    if (!panel.contains(e.target) && !trigger.contains(e.target)) closePanel();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      closePanel();
      trigger.focus();
    }
  }

  trigger.addEventListener('click', (e) => {
    if (!desktopQuery.matches) return; // mobile: let it navigate to the real page
    e.preventDefault();
    if (panel.hidden) openPanel();
    else closePanel();
  });

  // Tabs (only meaningful while the search box is empty — see setSearchMode).
  function activateTab(tab) {
    tabs.forEach((t) => {
      const selected = t === tab;
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
      t.tabIndex = selected ? 0 : -1;
    });
    const targetId = tab.getAttribute('aria-controls');
    tabPanels.forEach((p) => {
      if (p === searchPanel) return;
      p.hidden = p.id !== targetId;
    });
  }

  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => activateTab(tab));
    tab.addEventListener('keydown', (e) => {
      let idx = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') idx = (i + 1) % tabs.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') idx = (i - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') idx = 0;
      else if (e.key === 'End') idx = tabs.length - 1;
      if (idx !== null) {
        e.preventDefault();
        tabs[idx].focus();
        activateTab(tabs[idx]);
      }
    });
  });

  // Search: swaps the tabs+panels view for a single filtered results panel.
  // searchPanel doesn't carry role="tabpanel" (it's not part of the
  // Popular/Country/Region tablist), so it's deliberately toggled on its own
  // here rather than via the tabPanels array — leaving it out of that array
  // meant it never got un-hidden and search always rendered blank.
  function setSearchMode(active) {
    const tabsWrap = panel.querySelector('[data-je-mega-tabs]');
    if (tabsWrap) tabsWrap.hidden = active;
    if (active) {
      tabPanels.forEach((p) => {
        p.hidden = true;
      });
      if (searchPanel) searchPanel.hidden = false;
    } else {
      if (searchPanel) searchPanel.hidden = true;
      const activeTab = tabs.find((t) => t.getAttribute('aria-selected') === 'true') || tabs[0];
      if (activeTab) activateTab(activeTab);
    }
  }

  if (searchInput && searchGrid) {
    searchInput.addEventListener('input', () => {
      const q = searchInput.value.trim().toLowerCase();
      if (!q) {
        setSearchMode(false);
        return;
      }
      setSearchMode(true);
      let visibleCount = 0;
      searchTiles.forEach((li) => {
        const nameEl = li.querySelector('.je-cp__tile-name');
        const name = nameEl ? nameEl.textContent.toLowerCase() : '';
        const match = name.includes(q);
        li.hidden = !match;
        if (match) visibleCount += 1;
      });
      if (searchEmpty) searchEmpty.hidden = visibleCount !== 0;
    });
  }
});
